import React, { useState, useEffect } from 'react';
import { X, Save, Copy, Calendar, Repeat, Plus, Trash2, Edit } from 'lucide-react';
import { api } from '../auth/authStore';
import toast from 'react-hot-toast';

interface TaskTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  template?: TaskTemplate | null;
  mode: 'create' | 'edit';
}

interface TaskTemplate {
  id: string;
  name: string;
  description: string;
  type: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  estimatedHours: number;
  billing: {
    feeType: string;
    fixedAmount?: number;
    billableRate?: number;
  };
  checklist: Array<{
    label: string;
    required: boolean;
    order: number;
  }>;
  tags: string[];
  defaultAssignees: {
    preparerId?: string;
    reviewerId?: string;
    submitterId?: string;
  };
  isRecurring: boolean;
  recurringConfig?: {
    frequency: 'WEEKLY' | 'BI_WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'ANNUALLY';
    interval: number;
    dayOfWeek?: number;
    dayOfMonth?: number;
    monthOfYear?: number;
    endDate?: string;
    maxOccurrences?: number;
  };
  createdAt: string;
  usageCount: number;
}

interface Client {
  id: string;
  displayName: string;
  legalName: string;
}

interface User {
  id: string;
  firstName: string;
  lastName: string;
}

export const TaskTemplateModal: React.FC<TaskTemplateModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  template,
  mode
}) => {
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'TAX_RETURN',
    priority: 'medium' as 'low' | 'medium' | 'high' | 'urgent',
    estimatedHours: '',
    billing: {
      feeType: 'HOURLY',
      fixedAmount: '',
      billableRate: '',
    },
    checklist: [] as Array<{ label: string; required: boolean }>,
    tags: [] as string[],
    defaultAssignees: {
      preparerId: '',
      reviewerId: '',
      submitterId: '',
    },
    isRecurring: false,
    recurringConfig: {
      frequency: 'MONTHLY' as 'WEEKLY' | 'BI_WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'ANNUALLY',
      interval: 1,
      dayOfWeek: 1,
      dayOfMonth: 1,
      monthOfYear: 1,
      endDate: '',
      maxOccurrences: '',
    }
  });
  const [newTag, setNewTag] = useState('');
  const [newChecklistItem, setNewChecklistItem] = useState('');

  useEffect(() => {
    const initializeModal = async () => {
      try {
        if (isOpen) {
          console.log('🔄 Initializing modal, mode:', mode, 'template:', template);
          await fetchClients();
          await fetchUsers();

          if (mode === 'edit' && template) {
            // Fetch complete template data for editing
            await fetchTemplateForEdit(template.id);
          }
        }
      } catch (error) {
        console.error('❌ Error initializing modal:', error);
      }
    };

    initializeModal();
  }, [isOpen, mode, template]);

  const fetchTemplateForEdit = async (templateId: string) => {
    try {
      console.log('🔍 Fetching template for edit:', templateId);
      const response = await api.get(`/firm-admin/task-templates/${templateId}`);
      console.log('📋 Template response:', response.data);

      if (response.data.success && response.data.data.template) {
        const template = response.data.data.template;
        console.log('✅ Setting form data with template:', template);

        setFormData({
          name: template.name || '',
          description: template.description || '',
          type: template.type || 'TAX_RETURN',
          priority: template.priority || 'medium',
          estimatedHours: template.estimatedHours?.toString() || '',
          billing: {
            feeType: template.billing?.feeType || 'HOURLY',
            fixedAmount: template.billing?.fixedAmount?.toString() || '',
            billableRate: template.billing?.billableRate?.toString() || '',
          },
          checklist: template.checklist?.map(item => ({
            label: item.label || '',
            required: item.required || false
          })) || [],
          tags: template.tags ? [...template.tags] : [],
          defaultAssignees: {
            preparerId: template.assignees?.defaultPreparerId || '',
            reviewerId: template.assignees?.defaultReviewerId || '',
            submitterId: template.assignees?.defaultSubmitterId || '',
          },
          isRecurring: template.isRecurring || false,
          recurringConfig: {
            frequency: template.recurringConfig?.frequency || 'MONTHLY',
            interval: template.recurringConfig?.interval || 1,
            dayOfWeek: template.recurringConfig?.dayOfWeek || 1,
            dayOfMonth: template.recurringConfig?.dayOfMonth || 1,
            monthOfYear: template.recurringConfig?.monthOfYear || 1,
            endDate: template.recurringConfig?.endDate?.split('T')[0] || '',
            maxOccurrences: template.recurringConfig?.maxOccurrences?.toString() || '',
          }
        });
      }
    } catch (error) {
      console.error('❌ Error fetching template for edit:', error);
      // Fallback to using the template data passed as prop
      if (template) {
        setFormData({
          name: template.name || '',
          description: template.description || '',
          type: template.type || 'TAX_RETURN',
          priority: template.priority || 'medium',
          estimatedHours: template.estimatedHours?.toString() || '',
          billing: {
            feeType: template.billing?.feeType || 'HOURLY',
            fixedAmount: template.billing?.fixedAmount?.toString() || '',
            billableRate: template.billing?.billableRate?.toString() || '',
          },
          checklist: template.checklist?.map(item => ({
            label: item.label || '',
            required: item.required || false
          })) || [],
          tags: template.tags ? [...template.tags] : [],
          defaultAssignees: {
            preparerId: template.assignees?.defaultPreparerId || '',
            reviewerId: template.assignees?.defaultReviewerId || '',
            submitterId: template.assignees?.defaultSubmitterId || '',
          },
          isRecurring: template.isRecurring || false,
          recurringConfig: {
            frequency: template.recurringConfig?.frequency || 'MONTHLY',
            interval: template.recurringConfig?.interval || 1,
            dayOfWeek: template.recurringConfig?.dayOfWeek || 1,
            dayOfMonth: template.recurringConfig?.dayOfMonth || 1,
            monthOfYear: template.recurringConfig?.monthOfYear || 1,
            endDate: template.recurringConfig?.endDate?.split('T')[0] || '',
            maxOccurrences: template.recurringConfig?.maxOccurrences?.toString() || '',
          }
        });
      }
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get('/clients');
      if (response.data.success) {
        setClients(response.data.data.clients || []);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/firm-admin/users');
      if (response.data.success) {
        setUsers(response.data.data.users || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Template name is required');
      return;
    }

    setLoading(true);
    
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        type: formData.type,
        priority: formData.priority,
        estimatedHours: parseFloat(formData.estimatedHours) || 0,
        billing: {
          feeType: formData.billing.feeType,
          ...(formData.billing.fixedAmount && { fixedAmount: parseFloat(formData.billing.fixedAmount) }),
          ...(formData.billing.billableRate && { billableRate: parseFloat(formData.billing.billableRate) }),
        },
        checklist: formData.checklist.map((item, index) => ({
          label: item.label,
          required: item.required,
          order: index,
        })),
        tags: formData.tags,
        assignees: {
          defaultPreparerId: formData.defaultAssignees.preparerId || null,
          defaultReviewerId: formData.defaultAssignees.reviewerId || null,
          defaultSubmitterId: formData.defaultAssignees.submitterId || null,
        },
        isRecurring: formData.isRecurring,
        ...(formData.isRecurring && {
          recurringConfig: {
            frequency: formData.recurringConfig.frequency,
            interval: formData.recurringConfig.interval,
            ...(formData.recurringConfig.frequency === 'WEEKLY' && { dayOfWeek: formData.recurringConfig.dayOfWeek }),
            ...(formData.recurringConfig.frequency === 'MONTHLY' && { dayOfMonth: formData.recurringConfig.dayOfMonth }),
            ...(formData.recurringConfig.frequency === 'ANNUALLY' && { 
              dayOfMonth: formData.recurringConfig.dayOfMonth,
              monthOfYear: formData.recurringConfig.monthOfYear 
            }),
            ...(formData.recurringConfig.endDate && { endDate: new Date(formData.recurringConfig.endDate).toISOString() }),
            ...(formData.recurringConfig.maxOccurrences && { maxOccurrences: parseInt(formData.recurringConfig.maxOccurrences) }),
          }
        })
      };

      const response = mode === 'create'
        ? await api.post('/firm-admin/task-templates', payload)
        : await api.patch(`/firm-admin/task-templates/${template?.id}`, payload);

      if (response.data.success) {
        toast.success(`Template ${mode === 'create' ? 'created' : 'updated'} successfully`);
        onSuccess();
        onClose();
      }
    } catch (error: any) {
      console.error(`Error ${mode === 'create' ? 'creating' : 'updating'} template:`, error);
      toast.error(error.response?.data?.message || `Failed to ${mode} template`);
    } finally {
      setLoading(false);
    }
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim().toLowerCase())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim().toLowerCase()]
      }));
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const addChecklistItem = () => {
    if (newChecklistItem.trim()) {
      setFormData(prev => ({
        ...prev,
        checklist: [...prev.checklist, { label: newChecklistItem.trim(), required: true }]
      }));
      setNewChecklistItem('');
    }
  };

  const removeChecklistItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      checklist: prev.checklist.filter((_, i) => i !== index)
    }));
  };

  if (!isOpen) return null;

  console.log('🎨 Rendering TaskTemplateModal, mode:', mode, 'template:', template);

  try {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">
              {mode === 'create' ? 'Create Task Template' : 'Edit Task Template'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Template Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="e.g., Monthly Bookkeeping Template"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Task Type
              </label>
              <select
                value={formData.type}
                onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="TAX_RETURN">Tax Return</option>
                <option value="BOOKKEEPING">Bookkeeping</option>
                <option value="PAYROLL">Payroll</option>
                <option value="AUDIT">Audit</option>
                <option value="CONSULTATION">Consultation</option>
                <option value="OTHER">Other</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Template description and instructions"
            />
          </div>

          {/* Priority and Estimated Hours */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Default Priority
              </label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value as any }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Estimated Hours
              </label>
              <input
                type="number"
                step="0.5"
                min="0"
                value={formData.estimatedHours}
                onChange={(e) => setFormData(prev => ({ ...prev, estimatedHours: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="0.0"
              />
            </div>
          </div>

          {/* Billing Configuration */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Billing Configuration</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fee Type
                </label>
                <select
                  value={formData.billing.feeType}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    billing: { ...prev.billing, feeType: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="HOURLY">Hourly</option>
                  <option value="FIXED">Fixed</option>
                  <option value="NO_CHARGE">No Charge</option>
                </select>
              </div>

              {formData.billing.feeType === 'FIXED' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fixed Amount ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.billing.fixedAmount}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      billing: { ...prev.billing, fixedAmount: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>
              )}

              {formData.billing.feeType === 'HOURLY' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Billable Rate ($/hour)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.billing.billableRate}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      billing: { ...prev.billing, billableRate: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Default Assignees */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Default Assignees</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Default Preparer
                </label>
                <select
                  value={formData.defaultAssignees.preparerId}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    defaultAssignees: { ...prev.defaultAssignees, preparerId: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">No default preparer</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.firstName} {user.lastName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Default Reviewer
                </label>
                <select
                  value={formData.defaultAssignees.reviewerId}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    defaultAssignees: { ...prev.defaultAssignees, reviewerId: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">No default reviewer</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.firstName} {user.lastName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Default Submitter
                </label>
                <select
                  value={formData.defaultAssignees.submitterId}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    defaultAssignees: { ...prev.defaultAssignees, submitterId: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">No default submitter</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.firstName} {user.lastName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Default Tags
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {formData.tags.map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    className="ml-1 text-blue-600 hover:text-blue-800"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Add a tag"
              />
              <button
                type="button"
                onClick={addTag}
                className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Checklist Template */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Default Checklist
            </label>
            <div className="space-y-2 mb-4">
              {formData.checklist.map((item, index) => (
                <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                  <input
                    type="checkbox"
                    checked={item.required}
                    onChange={(e) => {
                      const newChecklist = [...formData.checklist];
                      newChecklist[index].required = e.target.checked;
                      setFormData(prev => ({ ...prev, checklist: newChecklist }));
                    }}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="flex-1 text-sm">{item.label}</span>
                  <button
                    type="button"
                    onClick={() => removeChecklistItem(index)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newChecklistItem}
                onChange={(e) => setNewChecklistItem(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addChecklistItem())}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Add checklist item"
              />
              <button
                type="button"
                onClick={addChecklistItem}
                className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Recurring Configuration */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <input
                type="checkbox"
                id="isRecurring"
                checked={formData.isRecurring}
                onChange={(e) => setFormData(prev => ({ ...prev, isRecurring: e.target.checked }))}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="isRecurring" className="text-lg font-medium text-gray-900">
                <Repeat className="w-5 h-5 inline mr-2" />
                Recurring Task Template
              </label>
            </div>

            {formData.isRecurring && (
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Frequency
                    </label>
                    <select
                      value={formData.recurringConfig.frequency}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        recurringConfig: { ...prev.recurringConfig, frequency: e.target.value as any }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="WEEKLY">Weekly</option>
                      <option value="BI_WEEKLY">Bi Weekly</option>
                      <option value="MONTHLY">Monthly</option>
                      <option value="QUARTERLY">Quarterly</option>
                      <option value="ANNUALLY">Annually</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Repeat Every
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="1"
                        value={formData.recurringConfig.interval}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          recurringConfig: { ...prev.recurringConfig, interval: parseInt(e.target.value) || 1 }
                        }))}
                        className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                      <span className="text-sm text-gray-600">
                        {formData.recurringConfig.frequency.toLowerCase()}(s)
                      </span>
                    </div>
                  </div>
                </div>

                {formData.recurringConfig.frequency === 'WEEKLY' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Day of Week
                    </label>
                    <select
                      value={formData.recurringConfig.dayOfWeek}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        recurringConfig: { ...prev.recurringConfig, dayOfWeek: parseInt(e.target.value) }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value={1}>Monday</option>
                      <option value={2}>Tuesday</option>
                      <option value={3}>Wednesday</option>
                      <option value={4}>Thursday</option>
                      <option value={5}>Friday</option>
                      <option value={6}>Saturday</option>
                      <option value={0}>Sunday</option>
                    </select>
                  </div>
                )}

                {(formData.recurringConfig.frequency === 'MONTHLY' || formData.recurringConfig.frequency === 'ANNUALLY') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Day of Month
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="31"
                      value={formData.recurringConfig.dayOfMonth}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        recurringConfig: { ...prev.recurringConfig, dayOfMonth: parseInt(e.target.value) || 1 }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                )}

                {formData.recurringConfig.frequency === 'ANNUALLY' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Month
                    </label>
                    <select
                      value={formData.recurringConfig.monthOfYear}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        recurringConfig: { ...prev.recurringConfig, monthOfYear: parseInt(e.target.value) }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      {Array.from({ length: 12 }, (_, i) => (
                        <option key={i + 1} value={i + 1}>
                          {new Date(2024, i, 1).toLocaleString('default', { month: 'long' })}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Date (Optional)
                    </label>
                    <input
                      type="date"
                      value={formData.recurringConfig.endDate}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        recurringConfig: { ...prev.recurringConfig, endDate: e.target.value }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Max Occurrences (Optional)
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={formData.recurringConfig.maxOccurrences}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        recurringConfig: { ...prev.recurringConfig, maxOccurrences: e.target.value }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Unlimited"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? 'Saving...' : (mode === 'create' ? 'Create Template' : 'Update Template')}
            </button>
          </div>
        </form>
      </div>
    </div>
    );
  } catch (error) {
    console.error('❌ Error rendering TaskTemplateModal:', error);
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-md w-full p-6">
          <h2 className="text-xl font-semibold text-red-600 mb-4">Error</h2>
          <p className="text-gray-700 mb-4">
            There was an error loading the template editor. Please try again.
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    );
  }
};
